import { Button, Flex, Modal, Text } from "@mantine/core";
import get from "lodash/get";
import { useMemo } from "react";

import SvgAlert from "../../../components/Icons/Alert";
import { theme } from "../../../utils/mantine/theme.ts";
import { DocumentInfo } from "../../IDPForgeryCheck/DocumentInfo/DocumentInfo.tsx";
import { DocumentDatabaseStatus } from "../types";
import { useStyles } from "./StatusWarningModal.styles";

interface StatusWarningModalProps {
  opened: boolean;
  onClose: () => void;
  onRetry: () => void;
  status: DocumentDatabaseStatus;
  documentType?: string;
  companyName?: string;
}

const StatusWarningModal = ({
  opened,
  onClose,
  onRetry,
  status,
  documentType,
  companyName,
}: StatusWarningModalProps) => {
  const { classes } = useStyles();

  const statusInfo = useMemo(() => {
    switch (status) {
      case DocumentDatabaseStatus.FAILED:
        return {
          title: "Document Processing Failed",
          message:
            "The document processing has failed. Please try uploading the document again.",
          color: get(theme.colors, "red.0"),
        };
      case DocumentDatabaseStatus.UNREADABLE:
        return {
          title: "Document Unreadable",
          message:
            "The document could not be read properly. Please ensure the document is clear and in a supported format, then try again.",
          color: get(theme.colors, "yellow.0"),
        };
      default:
        return {
          title: "Document Issue",
          message:
            "There was an issue processing this document. Please try again.",
          color: get(theme.colors, "red.0"),
        };
    }
  }, [status]);

  return (
    <Modal opened={opened} onClose={onClose} centered size="md">
      <Flex direction="column" gap={30}>
        <Flex direction="column" align="center" gap={20}>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/*@ts-ignore*/}
          <SvgAlert width={60} height={60} fill={statusInfo.color} />

          <Text size="xl" align="center">
            {statusInfo.title}
          </Text>

          <Text size="md" align="center" className={classes.message}>
            {statusInfo.message}
          </Text>

          {documentType && companyName && (
            <DocumentInfo>
              <Text size="sm">
                <strong>Document Type:</strong> {documentType}
              </Text>
              <Text size="sm">
                <strong>Company:</strong> {companyName}
              </Text>
            </DocumentInfo>
          )}
        </Flex>
        <Flex gap={20} justify="flex-end">
          <Button variant="outlineBlue" uppercase onClick={onClose}>
            cancel
          </Button>
          <Button variant="filledBlue" uppercase onClick={onRetry}>
            Try Again
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default StatusWarningModal;
